import { useState } from 'react';
import type { UpdaterState } from '@teachpartner/contracts';
import { ApiError, describeError, type UpdateAccepted } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';

interface Pending {
  readonly kind: 'deploy' | 'rollback';
  readonly version: string;
}

/** 409: another run is in progress; 503: central-api can't reach the updater. */
export function describeUpdateError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'Another update run is in progress. Wait for it to finish, then try again.';
  }
  if (error instanceof ApiError && error.status === 503) {
    return 'The updater is unavailable right now. Try again in a minute.';
  }
  return describeError(error);
}

function ConfirmStep({
  pending,
  onConfirm,
  onCancel,
}: {
  pending: Pending;
  onConfirm(): void;
  onCancel(): void;
}) {
  const deploy = pending.kind === 'deploy';
  const label = deploy ? `Deploy ${pending.version}` : `Roll back to ${pending.version}`;
  return (
    <div className="banner banner-warn" role="alertdialog" aria-label={`Confirm: ${label}`}>
      <p>
        {deploy ? (
          <>
            Deploy release <strong>{pending.version}</strong> to central now? Central restarts; a
            failed health check rolls back automatically.
          </>
        ) : (
          <>
            Roll central back to release <strong>{pending.version}</strong>? Central restarts.
          </>
        )}
      </p>
      <button type="button" className={deploy ? undefined : 'danger'} onClick={onConfirm}>
        Confirm: {label}
      </button>
      <button type="button" className="link" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function ActionButtons({
  state,
  disabled,
  onCheck,
  onAsk,
}: {
  state: UpdaterState;
  disabled: boolean;
  onCheck(): void;
  onAsk(pending: Pending): void;
}) {
  const latest = state.latest?.version ?? null;
  const previous = state.previous;
  return (
    <>
      <button type="button" disabled={disabled} onClick={onCheck}>
        Check now
      </button>
      {latest !== null && latest !== state.current && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAsk({ kind: 'deploy', version: latest })}
        >
          Deploy {latest}
        </button>
      )}
      {previous !== null && (
        <button
          type="button"
          className="danger"
          disabled={disabled}
          onClick={() => onAsk({ kind: 'rollback', version: previous })}
        >
          Roll back to {previous}
        </button>
      )}
    </>
  );
}

/** Check now, Deploy the latest checked release, Roll back (both confirmed in the page). */
export function UpdateActions({
  state,
  onStarted,
}: {
  state: UpdaterState;
  onStarted(runId: string): void;
}) {
  const { api } = useServices();
  const [pending, setPending] = useState<Pending | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabled = state.busy || sending;

  const start = async (action: () => Promise<UpdateAccepted>) => {
    setPending(null);
    setSending(true);
    setError(null);
    try {
      onStarted((await action()).runId);
    } catch (e) {
      setError(describeUpdateError(e));
    } finally {
      setSending(false);
    }
  };

  const confirm = (p: Pending) =>
    void start(() => (p.kind === 'deploy' ? api.deployRelease(p.version) : api.rollbackRelease()));

  return (
    <section aria-label="Actions">
      <h2>Actions</h2>
      {state.busy && <p className="muted">A run is in progress; actions are paused.</p>}
      <ErrorBanner message={error} />
      <ActionButtons
        state={state}
        disabled={disabled}
        onCheck={() => void start(() => api.checkUpdates())}
        onAsk={setPending}
      />
      {pending && !disabled && (
        <ConfirmStep
          pending={pending}
          onConfirm={() => confirm(pending)}
          onCancel={() => setPending(null)}
        />
      )}
    </section>
  );
}
